import React, {useCallback} from 'react';

import type {AutocompleteGetTagProps} from '@mui/material';
import ValueChip, {
	type ValueChipProps,
} from '@netx/core-components/SelectField/components/ValueChip';
import {ChipSkeleton} from '@netx/core-components/Chip';

const defaultIsValueTagRemovable = () => true;

export interface AutoListSelectionProps<T> {
	disabled?: boolean;
	loading?: boolean;
	uncontrolledValues: string[];
	values: T[];
	getTagProps: AutocompleteGetTagProps;
	getValueLabel?: (value: T) => string;
	onSearch?: (value: string) => void;
	isValueTagRemovable?: (value: T) => boolean;
}

export const AutoListFieldSelectionPrimitive = <T,>({
	getTagProps,
	getValueLabel,
	loading,
	onSearch,
	uncontrolledValues,
	values,
	isValueTagRemovable = defaultIsValueTagRemovable,
	...props
}: AutoListSelectionProps<T>) => {
	const _getValueLabel = useCallback(
		(value: T) => {
			if (getValueLabel) return getValueLabel(value);
			return typeof value === 'string' ? value : '';
		},
		[getValueLabel],
	);

	if (loading) return <ChipSkeleton count={3} />;

	return (
		<>
			{values.map((value, index) => {
				const label = _getValueLabel(value);
				const uncontrolledValue = uncontrolledValues.includes(label);
				const valueChipProps: Omit<ValueChipProps, 'classes'> = {
					...getTagProps({index}),
					...props,
				};
				if (!isValueTagRemovable(value)) {
					delete valueChipProps.onDelete;
				}
				return (
					<ValueChip
						{...valueChipProps}
						onSearch={onSearch}
						uncontrolledValue={uncontrolledValue}
						value={label}
					/>
				);
			})}
		</>
	);
};

export const AutoListFieldSelection = React.memo(
	AutoListFieldSelectionPrimitive,
) as <T>(props: AutoListSelectionProps<T>) => JSX.Element;

export default AutoListFieldSelection;
